
import { HEADING_MASK, headingFromRadians } from "../sim/fixed";
import { Gait } from "../sim/movement";
import type { Intent } from "../input";

export const ARRIVAL_RADIUS_MM = 400;

export const STALL_MS = 1_500;

export const PROGRESS_MM = 50;

/** Where the player asked to be, and how near is near enough. */
export interface WalkGoal {
  readonly xMm: number;
  readonly yMm: number;
  /** Stop this far away. `ARRIVAL_RADIUS_MM` for ground, a talk range for an
   *  NPC -- walking *into* a townsperson is not what a click on one means. */
  readonly stopMm: number;
}

export function headingToward(dxMm: number, dyMm: number): number {
  return headingFromRadians(Math.atan2(dyMm, dxMm)) & HEADING_MASK;
}

/** Straight-line distance in millimetres. Float, because nothing downstream of
 *  it enters the integer simulation -- only the heading does, and that is
 *  quantised to 4,096 steps anyway. */
export function distanceToMm(fromXMm: number, fromYMm: number, goal: WalkGoal): number {
  return Math.hypot(goal.xMm - fromXMm, goal.yMm - fromYMm);
}

export class WalkTo {
  private goal: WalkGoal | null = null;
  /** Where the walker was when it last covered ground, and when. Together they
   *  are the whole of the stall detector -- see the module docstring for why it
   *  watches the walker rather than the remaining distance. */
  private markXMm = 0;
  private markYMm = 0;
  private markAtMs = 0;
  /** Has this goal been sampled yet? The stall window must measure *walking*
   *  time and not the wait for the first frame after the click -- see
   *  `intent`. */
  private sampled = false;

  /** Where the player asked to be, or null. */
  get destination(): WalkGoal | null {
    return this.goal;
  }

  get walking(): boolean {
    return this.goal !== null;
  }

  setDestination(
    fromXMm: number,
    fromYMm: number,
    goal: WalkGoal,
    nowMs: number,
  ): boolean {
    if (distanceToMm(fromXMm, fromYMm, goal) <= goal.stopMm) {
      this.goal = null;
      return false;
    }
    this.goal = goal;
    this.markXMm = fromXMm;
    this.markYMm = fromYMm;
    this.markAtMs = nowMs;
    this.sampled = false;
    return true;
  }

  follow(goal: WalkGoal): void {
    if (this.goal === null) return;
    this.goal = goal;
  }

  /** Forget the goal. Called by every control a person can steer with: a hand
   *  on the keys is a person who has changed their mind. */
  clear(): void {
    this.goal = null;
    this.sampled = false;
  }

  intent(fromXMm: number, fromYMm: number, nowMs: number): Intent | null {
    const goal = this.goal;
    if (goal === null) return null;

    if (distanceToMm(fromXMm, fromYMm, goal) <= goal.stopMm) {
      this.clear();
      return null;
    }

    if (!this.sampled) {
      this.sampled = true;
      this.markXMm = fromXMm;
      this.markYMm = fromYMm;
      this.markAtMs = nowMs;
    } else if (Math.hypot(fromXMm - this.markXMm, fromYMm - this.markYMm) >= PROGRESS_MM) {
      this.markXMm = fromXMm;
      this.markYMm = fromYMm;
      this.markAtMs = nowMs;
    } else if (nowMs - this.markAtMs >= STALL_MS) {
      // Not going anywhere. A wall, in practice: the server refuses the step
      // and the character walks on the spot. This class does not pathfind --
      // see the module docstring -- so the honest answer is to stop asking.
      this.clear();
      return null;
    }

    return {
      heading: headingToward(goal.xMm - fromXMm, goal.yMm - fromYMm),
      gait: Gait.RUN,
    };
  }
}
